import { RedirectLoopBreaker } from './loopBreaker';
import type { KV } from './types';

/**
 * sessionStorage-backed KV for the redirect loop breaker.
 *
 * Every accessor is guarded: storage throws outright in private-mode Safari
 * and whenever site data is blocked. Failing closed there would be worse than
 * the bug we're fixing — it would block login for everyone with cookies
 * disabled — so a storage failure degrades to "no loop protection" rather
 * than "no auth".
 */
const sessionKV: KV = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* no-op: see above */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* no-op: see above */
    }
  },
  keys() {
    try {
      return Object.keys(window.sessionStorage);
    } catch {
      return [];
    }
  },
};

/** Matches the createAuthClient defaults: 3 redirects per 2 minutes. */
export const MAX_AUTH_REDIRECTS = 3;
export const AUTH_REDIRECT_WINDOW_MS = 2 * 60 * 1000;

/**
 * Process-wide loop breaker for apps that drive the OAuth redirect themselves
 * rather than through `createAuthClient` (web-app and web-app-v3). Counting
 * lives in per-tab session storage, so one wedged tab can't lock out the rest.
 */
export const authRedirectLoopBreaker = new RedirectLoopBreaker(
  sessionKV,
  { now: () => Date.now() },
  MAX_AUTH_REDIRECTS,
  AUTH_REDIRECT_WINDOW_MS,
);

/**
 * How long a committed navigation to the IdP is allowed to take before we stop
 * believing in it. A real full-page redirect tears this document down long
 * before this elapses.
 */
export const REDIRECT_SETTLE_MS = 5000;

/**
 * Collapses a burst of concurrent hand-offs to the IdP into one. Several
 * independent failure handlers can decide to re-authenticate in the same tick
 * (a page-load's worth of requests all 401ing at once); without this, each one
 * records a redirect and the loop breaker trips on what is really a single
 * hand-off.
 *
 * The claim is deliberately NOT permanent. Assuming "the page always unloads"
 * is wrong in two ways:
 *   - the back/forward cache restores this document with its JS state intact,
 *     so a user who lands on the IdP login page and hits Back returns with the
 *     claim still set;
 *   - a navigation can simply never happen (blocked by the browser, an
 *     embedded webview).
 * Either way a permanent claim wedges auth silently — no redirect, no error —
 * until a hard reload, which is the same stuck state the loop breaker exists
 * to prevent. So the claim expires, and a bfcache restore releases it at once.
 */
export class RedirectInFlightGuard {
  private committedAt: number | null = null;

  constructor(private readonly settleMs: number = REDIRECT_SETTLE_MS) {
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', (event) => {
        if ((event as PageTransitionEvent).persisted) this.release();
      });
    }
  }

  /** True while a hand-off to the IdP is still plausibly in progress. */
  get active(): boolean {
    if (this.committedAt === null) return false;
    if (Date.now() - this.committedAt >= this.settleMs) {
      this.committedAt = null;
      return false;
    }
    return true;
  }

  claim(): void {
    this.committedAt = Date.now();
  }

  release(): void {
    this.committedAt = null;
  }
}

export const authRedirectInFlight = new RedirectInFlightGuard();
